import { useState, useMemo, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Search, Star, SlidersHorizontal, X } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { apiRequest } from "@/lib/queryClient";
import type { PlayerRanking } from "../../../shared/schema";
import PlayerSheetPN from "./player-sheet-pn";
import PlayersFilterSheetPN, { DEFAULT_FILTERS, applyPlayerFilters, activeFilterCount, type PlayerFilters } from "./players-filter-sheet-pn";
import TournoisView from "./tournois-view";
import TerrainsView from "./terrains-view";

type MainTab = "joueurs" | "tournois" | "terrains";

export type FftResult = {
  idCrm?: string | null; nom?: string; prenom?: string; licence?: string | null;
  rank?: number | null; evolution?: number | null; points?: number | null; tournaments?: number | null;
  age?: number | string | null; club?: string | null;
  isRegistered?: boolean; localId?: string | null; localElo?: number | null;
};

// Plage de rang FFT pour le filtre Classement : [minRank, maxRank].
const rankRange = (c: string): [number | null, number | null] => (({
  "Top 100": [null, 100], "Top 500": [null, 500], "Top 1000": [null, 1000],
  "100-200": [101, 200], "200-500": [201, 500], "500-1000": [501, 1000],
  "1000-10k": [1001, 10000], "10k+": [10001, null], "Tous": [null, null],
} as Record<string, [number | null, number | null]>)[c] || [null, null]);

// Plage d'âge FFT : [minAge, maxAge].
const ageRange = (a: string): [number | null, number | null] => (({
  "-18 ans": [null, 17], "18-25": [18, 25], "26-35 ans": [26, 35],
  "36-45": [36, 45], "45+": [46, null], "Tous": [null, null],
} as Record<string, [number | null, number | null]>)[a] || [null, null]);

const tierLevel = (elo: number) =>
  [1149, 1299, 1399, 1499, 1599, 1749, 1999].findIndex((m) => elo <= m) + 1 || 8;
const rankColor = (r: number) => (+r === 1 ? "text-pn-star" : +r === 2 ? "text-slate-400" : +r === 3 ? "text-amber-700" : "text-foreground");
const fftName = (r: FftResult) => [r.prenom, r.nom].filter(Boolean).join(" ") || "—";

function Segmented({ value, onChange }: { value: MainTab; onChange: (v: MainTab) => void }) {
  const { t } = useTranslation();
  const tabs: { id: MainTab; label: string }[] = [
    { id: "joueurs", label: t("players.tab.players", "Joueurs") },
    { id: "tournois", label: t("players.tab.tournaments", "Tournois") },
    { id: "terrains", label: t("players.tab.courts", "Terrains") },
  ];
  return (
    <div className="flex bg-pn-navy rounded-full p-1.5 gap-1 mx-1 mt-2 font-head">
      {tabs.map((tb) => (
        <button key={tb.id} onClick={() => onChange(tb.id)}
          className={`flex-1 py-3 rounded-full font-bold text-base transition ${value === tb.id ? "bg-white text-pn-navy" : "text-white"}`}>
          {tb.label}
        </button>
      ))}
    </div>
  );
}

const followKey = (playerId?: string | null) => (playerId ? `followedPlayers_${playerId}` : "followedPlayers");

export default function PlayersTabPN({ playerId, onNavigateToClub }: { playerId?: string | null; onNavigateToClub?: (clubName: string) => void }) {
  const { t } = useTranslation();
  const [tab, setTab] = useState<MainTab>("joueurs");
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [favOnly, setFavOnly] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const [openFft, setOpenFft] = useState<FftResult | null>(null);
  const [filters, setFilters] = useState<PlayerFilters>(DEFAULT_FILTERS);
  const [filterOpen, setFilterOpen] = useState(false);
  const [fav, setFav] = useState<Set<string>>(() => {
    try { return new Set(JSON.parse(localStorage.getItem(followKey(playerId)) || "[]")); } catch { return new Set(); }
  });

  const fftMode = !filters.padeloOnly; // toggle Padelo décoché = recherche base FFT

  useEffect(() => {
    const id = setTimeout(() => setDebouncedQ(q.trim()), 350);
    return () => clearTimeout(id);
  }, [q]);

  const toggleFav = (id: string) => {
    setFav((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      try { localStorage.setItem(followKey(playerId), JSON.stringify([...next])); } catch { /* storage indispo */ }
      return next;
    });
  };

  // --- Base PadELO (classement) ---
  const { data: players = [], isLoading } = useQuery<PlayerRanking[]>({
    queryKey: ["/api/players/rankings", "all"],
    queryFn: async () => (await apiRequest("GET", `/api/players/rankings?timeFilter=all`)).json(),
    staleTime: 0,
    enabled: tab === "joueurs" && !fftMode,
  });
  const base = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return players.filter((p) => (!needle || p.name.toLowerCase().includes(needle)) && (!favOnly || fav.has(p.id)));
  }, [players, q, favOnly, fav]);
  const list = useMemo(() => applyPlayerFilters(base, filters), [base, filters]);

  // --- Base FFT : liste par défaut (triée rang) OU recherche par nom (>=2 car.) ---
  const fftQ = debouncedQ.length >= 2 ? debouncedQ : "";
  const [minR, maxR] = rankRange(filters.classement);
  const [minA, maxA] = ageRange(filters.age);
  const sx = filters.sexe === "Hommes" ? "H" : filters.sexe === "Femmes" ? "F" : "";
  const { data: fft, isLoading: fftLoading } = useQuery<{ results: FftResult[]; hasMore: boolean }>({
    queryKey: ["/api/players/search/fft", fftQ, minR, maxR, sx, minA, maxA],
    queryFn: async () => {
      const u = `/api/players/search/fft?q=${encodeURIComponent(fftQ)}&offset=0`
        + (minR ? `&minRank=${minR}` : "") + (maxR ? `&maxRank=${maxR}` : "") + (sx ? `&sexe=${sx}` : "")
        + (minA ? `&minAge=${minA}` : "") + (maxA ? `&maxAge=${maxA}` : "");
      return (await apiRequest("GET", u)).json();
    },
    enabled: tab === "joueurs" && fftMode,
  });
  const fftResults = fft?.results ?? [];
  const fftList = favOnly ? fftResults.filter((r) => r.localId && fav.has(r.localId)) : fftResults;

  // Chips de filtres actifs, retirables. Vignette "Padelo" seulement si coché (décoché = base FFT, pas de chip).
  const activeChips: { key: string; label: string; clear: () => void }[] = [];
  if (filters.padeloOnly) activeChips.push({ key: "mode", label: "Padelo", clear: () => setFilters((f) => ({ ...f, padeloOnly: false })) });
  if (filters.sexe) activeChips.push({ key: "sexe", label: filters.sexe, clear: () => setFilters((f) => ({ ...f, sexe: null })) });
  if (filters.classement !== "Tous") activeChips.push({ key: "classement", label: filters.classement, clear: () => setFilters((f) => ({ ...f, classement: "Tous" })) });
  if (filters.ligue !== "Toutes") activeChips.push({ key: "ligue", label: filters.ligue, clear: () => setFilters((f) => ({ ...f, ligue: "Toutes" })) });
  if (filters.age !== "Tous") activeChips.push({ key: "age", label: filters.age, clear: () => setFilters((f) => ({ ...f, age: "Tous" })) });

  return (
    <div className="font-body pb-6">
      <Segmented value={tab} onChange={setTab} />

      {tab === "joueurs" && (
        <>
          <div className="px-1 pt-3 pb-2 flex items-center gap-2.5">
            <div className="flex items-center gap-3 bg-muted border border-border rounded-full px-4 py-3 flex-1 min-w-0">
              <Search className="w-5 h-5 text-pn-blue shrink-0" />
              <input value={q} onChange={(e) => setQ(e.target.value)}
                placeholder={fftMode ? t("players.searchFft", "Rechercher dans la base FFT") : t("players.searchPadelo", "Rechercher dans la base Padelo")}
                className="flex-1 min-w-0 bg-transparent outline-none text-base text-foreground placeholder:text-muted-foreground" />
              <button onClick={() => setFavOnly((v) => !v)} aria-label="Favoris" aria-pressed={favOnly} className="shrink-0">
                <Star className={`w-6 h-6 ${favOnly ? "fill-pn-star text-pn-star" : "text-muted-foreground"}`} />
              </button>
            </div>
            <button onClick={() => setFilterOpen(true)} aria-label="Filtres"
              className="relative shrink-0 w-14 h-14 rounded-2xl bg-pn-blue text-white flex items-center justify-center">
              <SlidersHorizontal className="w-6 h-6" />
              {activeFilterCount(filters) > 0 && (
                <span className="absolute -top-1.5 -right-1.5 bg-pn-lime text-pn-navy text-xs font-bold rounded-full min-w-5 h-5 px-1 flex items-center justify-center">{activeFilterCount(filters)}</span>
              )}
            </button>
          </div>

          {activeChips.length > 0 && (
            <div className="flex gap-2.5 overflow-x-auto px-1 pb-2 [&::-webkit-scrollbar]:hidden">
              {activeChips.map((c) => (
                <button key={c.key} onClick={c.clear}
                  className="shrink-0 inline-flex items-center gap-2 bg-pn-blue text-white rounded-full px-4 py-2 font-semibold text-sm">
                  {c.label} <X className="w-4 h-4" />
                </button>
              ))}
            </div>
          )}

          {fftMode ? (
            /* ---- Mode FFT : liste par défaut (triée rang) + recherche par nom ---- */
            fftLoading ? (
              <div className="space-y-3 px-1">{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-20 w-full rounded-2xl" />)}</div>
            ) : fftList.length === 0 ? (
              <p className="text-center text-muted-foreground py-10">{favOnly ? t("players.tab.noFavorites", "Aucun favori") : t("players.fftNoResult", "Aucun joueur FFT trouvé")}</p>
            ) : (
              <div className="space-y-3 px-1">
                {fftList.map((r, i) => {
                  const name = fftName(r);
                  const clickable = !!r.idCrm || (r.isRegistered && !!r.localId); // idCrm suffit pour la fiche FFT
                  return (
                    <div key={`fft-${i}`} className="flex items-center gap-3 bg-card border border-border rounded-2xl p-4 shadow-sm">
                      <button onClick={() => { if (clickable) { setOpenFft(r); setOpenId(r.localId ?? null); } }} disabled={!clickable}
                        className={`flex items-center gap-4 flex-1 min-w-0 text-left ${clickable ? "" : "cursor-default"}`}>
                        <div className={`font-head font-extrabold text-2xl min-w-[44px] ${rankColor(Number(r.rank))}`}>#{r.rank ?? "-"}</div>
                        <div className="flex-1 min-w-0">
                          <div className="font-bold text-foreground text-lg truncate">{name}</div>
                          <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                            {r.points != null && <span className="inline-block border border-pn-blue text-pn-blue rounded-full px-3 py-0.5 font-bold text-sm">{r.points} pts</span>}
                            {r.evolution != null && r.evolution !== 0 && (
                              <span className={`text-sm font-semibold ${r.evolution > 0 ? "text-green-600" : "text-red-500"}`}>{r.evolution > 0 ? "▲" : "▼"}{Math.abs(r.evolution)}</span>
                            )}
                            {r.isRegistered && r.localElo != null && <span className="inline-block bg-pn-lime text-pn-navy rounded-full px-2.5 py-0.5 font-bold text-xs">{t("players.levelShort", "Niv. {{n}}", { n: tierLevel(r.localElo) })}</span>}
                          </div>
                        </div>
                      </button>
                      {r.localId && (
                        <button onClick={() => toggleFav(r.localId!)} aria-label="Favori" className="shrink-0 p-1">
                          <Star className={`w-6 h-6 ${fav.has(r.localId) ? "fill-pn-star text-pn-star" : "text-muted-foreground"}`} />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )
          ) : (
            /* ---- Mode PadELO : classement ---- */
            <>
              <p className="px-2 pb-2 font-bold text-muted-foreground">
                {favOnly ? t("players.tab.favorites", "Favoris") + " · " : ""}{list.length} {t("players.countLabel", "joueurs")}
              </p>
              {isLoading ? (
                <div className="space-y-3 px-1">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-24 w-full rounded-2xl" />)}</div>
              ) : (
                <div className="space-y-3 px-1">
                  {list.map((p) => (
                    <div key={p.id} className="flex items-center gap-3 bg-card border border-border rounded-2xl p-4 shadow-sm">
                      <button onClick={() => setOpenId(p.id)} className="flex items-center gap-4 flex-1 min-w-0 text-left">
                        <div className={`font-head font-extrabold text-2xl min-w-[44px] ${rankColor(Number(p.rank))}`}>#{p.rank}</div>
                        <div className="flex-1 min-w-0">
                          <div className="font-bold text-foreground text-lg truncate">{p.name}</div>
                          <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                            <span className="inline-block border border-pn-blue text-pn-blue rounded-full px-3 py-0.5 font-bold text-sm">{p.eloRating} pts</span>
                            <span className="inline-block bg-pn-lime text-pn-navy rounded-full px-2.5 py-0.5 font-bold text-xs">{t("players.levelShort", "Niv. {{n}}", { n: tierLevel(p.eloRating) })}</span>
                            {p.matchesPlayed > 0 && <span className="text-sm text-muted-foreground">{t("players.wrShort", "{{n}}% WR", { n: Math.round((p.matchesWon / p.matchesPlayed) * 100) })}</span>}
                          </div>
                        </div>
                      </button>
                      <button onClick={() => toggleFav(p.id)} aria-label="Favori" className="shrink-0 p-1">
                        <Star className={`w-6 h-6 ${fav.has(p.id) ? "fill-pn-star text-pn-star" : "text-muted-foreground"}`} />
                      </button>
                    </div>
                  ))}
                  {list.length === 0 && <p className="text-center text-muted-foreground py-10">{favOnly ? t("players.tab.noFavorites", "Aucun favori") : t("ranking.noResults", "Aucun joueur")}</p>}
                </div>
              )}
            </>
          )}

          <PlayerSheetPN playerId={openId} fft={openFft} open={!!openId || !!openFft} onClose={() => { setOpenId(null); setOpenFft(null); }} onNavigateToClub={onNavigateToClub} onOpenPlayer={(id) => { setOpenFft(null); setOpenId(id); }} />
          {filterOpen && (
            <PlayersFilterSheetPN
              initial={filters}
              resultCount={(f) => (f.padeloOnly ? applyPlayerFilters(base, f).length : fftResults.length)}
              onApply={(f) => { setFilters(f); setFilterOpen(false); }}
              onClose={() => setFilterOpen(false)}
            />
          )}
        </>
      )}

      {tab === "tournois" && <TournoisView playerId={playerId} />}
      {tab === "terrains" && <TerrainsView />}
    </div>
  );
}
